import {FunctionComponent, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import Button from '@/components/button';
import Link from '@/components/link';
import PopupMenu from '@/components/popup-menu';
import {BUTTON_VARIANT} from '@/constants/button';
import {useAuthentication} from '@/contexts/authentication';
import {TrackingClickPrefix} from '@/services/analytics/analytics-types';
import {WEB_CLICK_TRACKING_KEY} from '@/services/analytics/web-click-tracking-key';

interface ElementProps {
  isVisible: boolean;
  onClose: () => void;
  toggleRef?: React.RefObject<HTMLButtonElement | null>;
}

const ProfileMenu: FunctionComponent<ElementProps> = ({isVisible, onClose, toggleRef}) => {
  const {t} = useTranslation();
  const {logout} = useAuthentication();

  const handleLinkClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleLogout = useCallback(async () => {
    await logout();
    onClose();
  }, [logout, onClose]);

  if (!isVisible) return null;

  return (
    <PopupMenu
      className="right-0 -mr-2 -mt-4 min-w-64 px-3"
      onClose={onClose}
      toggleRef={toggleRef}
      data-testid="profile-menu"
    >
      <Link
        to="profile"
        onClick={handleLinkClick}
        className="block w-full rounded-lg p-3 leading-4 hover:bg-greige"
        data-testid="menu-profile-link"
        trackingKey={WEB_CLICK_TRACKING_KEY.VIEW_MY_PROFILE}
      >
        {t('components.profile-menu.my-profile')}
      </Link>

      <div className="mt-6 px-3">
        <Button
          className="w-full"
          variant={BUTTON_VARIANT.FULL_WIDTH}
          onClick={handleLogout}
          label={t('components.profile-menu.logout')}
          trackingKey={WEB_CLICK_TRACKING_KEY.LOGOUT}
          trackingAs={TrackingClickPrefix.NAV}
          data-testid="menu-profile-logout"
        />
      </div>
    </PopupMenu>
  );
};

export default ProfileMenu;
